import React from 'react';
import { motion } from 'motion/react';
import { useLanguage } from '../context/LanguageContext';
import { Layout, Palette, Code2, Database, Globe2, CheckCircle2 } from 'lucide-react';

const Skills = () => {
  const { t } = useLanguage();

  const technologies = [
    { name: 'PHP', level: 90 },
    { name: 'JavaScript', level: 90 },
    { name: 'MySQL', level: 80 },
    { name: 'HTML', level: 95 },
    { name: 'CSS', level: 90 },
    { name: 'Laravel', level: 80 },
    { name: 'Bootstrap', level: 80 },
    { name: 'React', level: 70 },
    { name: 'Node.js', level: 60 },    
    { name: 'jQuery', level: 75 },
    { name: 'Firebase', level: 65 },
    { name: 'VBA', level: 80 },
  ];

  const cms = [
    { name: 'WordPress', icon: Globe2 },
    { name: 'Shopify', icon: Layout },
    { name: 'Magento', icon: Database },
    { name: 'Wix', icon: Layout },
    { name: 'Joomla', icon: Globe2 },
  ];

  const multimedia = [
    'Photoshop', 'Illustrator', 'Camtasia', 'After Effects', 'OBS Studio'
  ];

  return (
    <section id="skills" className="py-24 relative overflow-hidden bg-white/5">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-display font-black mb-4 tracking-tight">
            {t('skills.title')}
          </h2>
          <div className="w-20 h-1 bg-primary mx-auto rounded-full" />
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Technologies */}
          <div>
            <h3 className="text-2xl font-display font-bold mb-8 flex items-center gap-3">
              <Code2 className="text-primary" />
              {t('skills.tech_title')}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {technologies.map((tech, idx) => (
                <motion.div
                  key={tech.name}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.05 }}
                  className="p-4 glass rounded-2xl group hover:border-primary/30 transition-all"
                >
                  <div className="flex justify-between items-center mb-3">
                    <span className="font-bold text-white/80">{tech.name}</span>
                    <span className="text-xs font-mono text-primary">{tech.level}%</span>
                  </div>
                  <div className="h-1.5 w-full bg-white/10 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: `${tech.level}%` }}
                      viewport={{ once: true }}
                      transition={{ duration: 1, delay: 0.5 }}
                      className="h-full bg-gradient-to-r from-primary to-secondary"
                    />
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* CMS & WordPress */}
          <div className="space-y-12">
            <div>
              <h3 className="text-2xl font-display font-bold mb-8 flex items-center gap-3">
                <Layout className="text-secondary" />
                {t('skills.cms_title')}
              </h3>
              <div className="flex flex-wrap gap-4">
                {cms.map((item, idx) => (
                  <motion.div
                    key={item.name}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.1 }}
                    className="px-6 py-3 glass rounded-full flex items-center gap-3 hover:border-primary/50 transition-all cursor-default"
                  >
                    <item.icon className="w-5 h-5 text-primary" />
                    <span className="font-bold text-white/80">{item.name}</span>
                  </motion.div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-display font-bold mb-8 flex items-center gap-3">
                <CheckCircle2 className="text-accent" />
                {t('skills.wp_skills_title')}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {t('skills.wp_skills').map((skill: string, idx: number) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.1 }}
                    className="flex items-center gap-3 text-white/70"
                  >
                    <div className="w-2 h-2 rounded-full bg-primary shadow-[0_0_8px_rgba(0,242,255,0.8)]" />
                    <span>{skill}</span>
                  </motion.div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-display font-bold mb-8 flex items-center gap-3">
                <Palette className="text-primary" />
                {t('skills.multimedia_title')}
              </h3>
              <div className="flex flex-wrap gap-3">
                {multimedia.map((item, idx) => (
                  <motion.span
                    key={idx}
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.1 }}
                    className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-sm font-medium hover:bg-white/10 transition-colors"
                  >
                    {item}
                  </motion.span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Skills;
